import type { Weekday } from "@qendo/util";
import type { DateArg } from "date-fns";

export enum ScheduleFrequency {
    daily = "d",
    specific_days = "s",
    interval = "i",
    cyclical = "c",
    as_needed = "n",
}

type ScheduleFrequencyItem<T extends ScheduleFrequency> = {
    value: T;
    label: string;
    subtitle?: string;
};
type ScheduleFrequencyItemRecord<T extends ScheduleFrequency> = Record<T, ScheduleFrequencyItem<T>>;

export const ScheduleFrequencyMap: ScheduleFrequencyItemRecord<ScheduleFrequency> = {
    [ScheduleFrequency.daily]: { value: ScheduleFrequency.daily, label: "Every day" },
    [ScheduleFrequency.specific_days]: {
        value: ScheduleFrequency.specific_days,
        label: "On specific days of the week",
        subtitle: "“On Monday, Wednesday & Thursday”",
    },
    [ScheduleFrequency.interval]: {
        value: ScheduleFrequency.interval,
        label: "On an interval",
        subtitle: "“Once every 3 days”",
    },
    [ScheduleFrequency.cyclical]: {
        value: ScheduleFrequency.cyclical,
        label: "On a cyclical schedule",
        subtitle: "“Take every day for 5 days and pause for 2 days”",
    },
    [ScheduleFrequency.as_needed]: { value: ScheduleFrequency.as_needed, label: "As needed" },
};

export enum Interval {
    day = "d",
    week = "w",
}

export const IntervalMap: Record<Interval, string> = {
    [Interval.day]: "Day",
    [Interval.week]: "Week",
};

export const IntervalOptions = [
    { value: Interval.day, label: IntervalMap[Interval.day] },
    { value: Interval.week, label: IntervalMap[Interval.week] },
];

export type WeekdayData = Record<Weekday, boolean>;

export const EmptyWeek: WeekdayData = {
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
};

export interface DosageScheduleTimeData {
    time: DateArg<Date>;
    amount: `${number}` | null;
}
export interface BasicScheduleTimeData {
    time: DateArg<Date>;
    amount?: never;
}

export type ScheduleTimeData = DosageScheduleTimeData | BasicScheduleTimeData;

export interface CycleData {
    every: Interval;
    use_for: number;
    pause_for: number;
}

export type DailySchedule = {
    frequency: ScheduleFrequency.daily;
    times: ScheduleTimeData[];
};
export type WeeklySchedule = {
    frequency: ScheduleFrequency.specific_days;
    days: WeekdayData;
    times: ScheduleTimeData[];
};
export type IntervalSchedule = {
    frequency: ScheduleFrequency.interval;
    interval: number;
    times: ScheduleTimeData[];
};
export type CycleSchedule = {
    frequency: ScheduleFrequency.cyclical;
    cycle: CycleData;
    times: ScheduleTimeData[];
};
export type AsNeededSchedule = {
    frequency: ScheduleFrequency.as_needed;
};
export type BlankSchedule = {
    frequency: ScheduleFrequency | null;
};

export type ScheduleData = {
    frequency: ScheduleFrequency | null;
    days?: WeekdayData | null;
    interval?: number | null;
    cycle?: CycleData | null;
    times?: ScheduleTimeData[];
} & (DailySchedule | WeeklySchedule | IntervalSchedule | CycleSchedule | AsNeededSchedule | BlankSchedule);
